import React from 'react';
import { Clock, MapPin, Pin } from 'lucide-react';
import UserAvatar from './UserAvatar';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import { generatePendencyId } from '../../utils/pendencyUtils';
import { motion } from 'framer-motion';

const PendencyListItem = ({ pendency, onCardClick, creatorUser, assignedUser, isPinned, onPinClick, canPin }) => {
    const pendencyId = pendency.displayId || generatePendencyId(pendency.id);

    const priorityClasses = {
        'Alta': 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
        'Média': 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
        'Baixa': 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
    };

    const timeAgo = formatTimeAgo(pendency.dataCadastro);
    const isExecuted = pendency.status === 'Executado';
    const statusOrTimeLabel = isExecuted ? 'Executado' : timeAgo?.text;

    const handlePinClick = (e) => {
        e.stopPropagation();
        onPinClick(pendency.id);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick(pendency);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
            onClick={() => onCardClick(pendency)}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Pendência #${pendencyId}: ${pendency.descricao}`}
            className={`
                flex items-center p-3 rounded-lg border cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                transition-colors duration-200
                ${isPinned ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-500' : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'}
                hover:border-blue-500 dark:hover:border-blue-400
                ${isExecuted ? 'opacity-60 hover:opacity-100' : ''}
            `}
        >
            <div className="grid grid-cols-12 gap-4 w-full items-center">
                {/* ID */}
                <div className="col-span-1">
                    <span className="text-xs font-semibold px-2 py-1 rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300">
                        #{pendencyId}
                    </span>
                </div>

                {/* Descrição */}
                <div className="col-span-4">
                    <p className="font-bold text-slate-800 dark:text-slate-100 truncate" title={pendency.descricao}>
                        {pendency.descricao}
                    </p>
                </div>

                {/* Cidade */}
                <div className="col-span-1 flex items-center text-sm text-slate-600 dark:text-slate-300">
                    <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                    {pendency.cidade}
                </div>

                {/* Prioridade */}
                <div className="col-span-1">
                    {!isExecuted && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${priorityClasses[pendency.prioridade]}`}>
                            {pendency.prioridade}
                        </span>
                    )}
                </div>

                {/* Tempo Decorrido */}
                <div className="col-span-2 flex items-center text-sm text-slate-500 dark:text-slate-400">
                     <Clock className="h-4 w-4 mr-2" />
                    {statusOrTimeLabel}
                </div>

                {/* Data de Cadastro */}
                <div className="col-span-1 text-sm text-slate-500 dark:text-slate-400 text-center">
                    {new Date(pendency.dataCadastro).toLocaleDateString()}
                </div>

                {/* Usuários */}
                <div className="col-span-2 flex items-center justify-end">
                    <div className="flex items-center -space-x-3">
                        {assignedUser && <UserAvatar user={assignedUser} />}
                        {creatorUser && <UserAvatar user={creatorUser} />}
                    </div>
                    {canPin && (
                        <button
                            onClick={handlePinClick}
                            className={`ml-3 p-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                                ${isPinned ? 'text-amber-600' : 'text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}
                                transition-colors z-10`}
                            title={isPinned ? "Desafixar" : "Fixar"}
                            aria-label={isPinned ? "Desafixar" : "Fixar"}
                        >
                            <Pin className={`h-5 w-5 ${isPinned ? 'fill-current' : ''}`} />
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

// Optimized: Wrapped in React.memo to prevent unnecessary re-renders when parent component (PendenciesPage) updates.
// This works in conjunction with stable callbacks (useCallback) passed as props.
export default React.memo(PendencyListItem);
